import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { Typography, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const APNCourseChart = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>No data available</p>
      </div>
    );
  }

  // Sort data by courseId to maintain proper sequence
  const sortedData = [...data].sort((a, b) => parseInt(a.courseId) - parseInt(b.courseId));
  
  // Find indices for reference lines
  const toadsIndex = sortedData.findIndex(item => item.course === "Toad's Turnpike");
  const warioIndex = sortedData.findIndex(item => item.course === "Wario Stadium");
  const dkIndex = sortedData.findIndex(item => item.course === "D.K.'s Jungle Parkway");
  
  // Find indices for transition points - check multiple possible spellings
  let kalamariIndex = sortedData.findIndex(item => item.course === "Kalimari Desert");
  if (kalamariIndex === -1) {
    kalamariIndex = sortedData.findIndex(item => item.course === "Kalamari Desert");
  }
  if (kalamariIndex === -1) {
    kalamariIndex = sortedData.findIndex(item => item.course.includes("alamari"));
  }
  
  const marioRacewayIndex = sortedData.findIndex(item => item.course === "Mario Raceway");
  const bowsersCastleIndex = sortedData.findIndex(item => item.course === "Bowser's Castle");
  
  // Debug indices
  console.log('Indices:', { 
    kalamariIndex, 
    toadsIndex, 
    marioRacewayIndex, 
    warioIndex, 
    bowsersCastleIndex, 
    dkIndex 
  });
  
  // Log the actual course names for debugging
  console.log('Course Names:', sortedData.map(item => item.course));
  
  // Cup boundaries for reference areas
  const cupBoundaries = [
    { start: 0, end: toadsIndex, color: 'rgba(222, 255, 222, 0.4)', name: 'Mushroom' },
    { start: toadsIndex, end: warioIndex, color: 'rgba(222, 222, 255, 0.4)', name: 'Flower' },
    { start: warioIndex, end: dkIndex, color: 'rgba(255, 222, 222, 0.4)', name: 'Star' },
    { start: dkIndex, end: sortedData.length - 1, color: 'rgba(255, 255, 222, 0.4)', name: 'Special' },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '8px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ marginBottom: '4px', fontWeight: '500' }}>{data.course}</p>
          <p>APN: {data.apn}</p>
          <p>Win %: {data.winPercent}%</p>
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    // Since lower is better for APN, we swap the highlighting logic
    const isMin = payload.apn === maxAPN; // Highest APN is worst (red)
    const isMax = payload.apn === minAPN; // Lowest APN is best (green)

    if (isMax) {
      return (
        <g>
          {/* Outer highlight circle */}
          <circle
            cx={cx}
            cy={cy}
            r={10}
            fill="rgba(75, 85, 99, 0.2)"
            stroke="#7eff85"
            strokeWidth={5}
          />
          {/* Inner dot */}
          <circle
            cx={cx}
            cy={cy}
            r={5}
            fill="white"
            strokeWidth={2}
          />
        </g>
      );
    } else if (isMin) {
      return (
        <g>
          {/* Outer highlight circle */}
          <circle
            cx={cx}
            cy={cy}
            r={10}
            fill="rgba(75, 85, 99, 0.2)"
            stroke="#ff907e"
            strokeWidth={5}
          />
          {/* Inner dot */}
          <circle
            cx={cx}
            cy={cy}
            r={5}
            fill="white"
            strokeWidth={2}
          />
        </g>
      );
    }

    // Regular dot
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="#c9feff"
        opacity={0.8}
      />
    );
  };

  const maxAPN = Math.max(...sortedData.map(item => Number(item.apn) || 0));
  const minAPN = Math.min(...sortedData.map(item => Number(item.apn) || 0));
  return (
    <div style={{ width: '100%', height: '400px', position: 'relative' }}>
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ marginTop: 1 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Press Start 2P", sans-serif', fontWeight: 'bold', fontSize: '1rem' }}>APN Across Courses</Typography>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={sortedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" fill='black' fillOpacity={0.6}/>
          
          {/* Cup Section Shading */}
          {cupBoundaries.map((cup, index) => (
            <ReferenceArea 
              key={`cup-${index}`}
              x1={sortedData[cup.start]?.course} 
              x2={sortedData[cup.end]?.course}
              fill={cup.color}
              fillOpacity={1.0}
              strokeOpacity={0}
              label={isMobile ? null : {
                value: cup.name,
                position: 'insideTopLeft',
                fill: '#000000',
                fontSize: 14,
                fontWeight: 'bold',
                dy: 20,
                dx: 10
              }}
            />
          ))}
          
          {/* Cup Transition Grey Areas - First transition hardcoded by index */}
          <ReferenceArea 
            key="transition-1"
            x1={sortedData[3]?.course} 
            x2={sortedData[4]?.course}
            fill="#555555"
            fillOpacity={0.5}
            strokeOpacity={0}
          />
          {marioRacewayIndex >= 0 && warioIndex > 0 && (
            <ReferenceArea 
              key="transition-2"
              x1={sortedData[marioRacewayIndex]?.course} 
              x2={sortedData[warioIndex]?.course}
              fill="#555555"
              fillOpacity={0.5}
              strokeOpacity={0}
            />
          )}
          {bowsersCastleIndex >= 0 && dkIndex > 0 && (
            <ReferenceArea 
              key="transition-3"
              x1={sortedData[bowsersCastleIndex]?.course} 
              x2={sortedData[dkIndex]?.course}
              fill="#555555"
              fillOpacity={0.5}
              strokeOpacity={0}
            />
          )}
          
          {/* Vertical Reference Lines */}
          {toadsIndex >= 0 && (
            <ReferenceLine 
              x={sortedData[toadsIndex].course} 
              stroke="#E5E7EB" 
              strokeWidth={2} 
              strokeDasharray="5 5" 
            />
          )}
          {warioIndex >= 0 && (
            <ReferenceLine 
              x={sortedData[warioIndex].course} 
              stroke="#E5E7EB" 
              strokeWidth={2} 
              strokeDasharray="5 5" 
            />
          )}
          {dkIndex >= 0 && (
            <ReferenceLine 
              x={sortedData[dkIndex].course} 
              stroke="#E5E7EB" 
              strokeWidth={2} 
              strokeDasharray="5 5" 
            />
          )}
          <XAxis
            dataKey="course"
            tickFormatter={(value) => {
              // Limit course names to 10 characters with ellipsis
              return value.length > 10 ? value.substring(0, 8) + '...' : value;
            }}
            tick={{ fill: '#6B7280' }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            label={{ value: 'Course', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 14 } }}
          />
          <YAxis
            domain={[4, 1]} // Fixed range: 4 at bottom, 1 at top
            tick={{ fill: '#6B7280' }}
            type='number'
            ticks={[1, 1.5, 2, 2.5, 3, 3.5, 4]} // Explicit ticks for race positions
            reversed={true} // Explicitly reverse the axis
            orientation="left"
            label={{ value: 'APN (Average Place Normalized)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 14 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="apn"
            stroke="#c9feff"
            strokeWidth={2}
            dot={<CustomDot />}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default APNCourseChart;
